// Lightweight particle system. Each particle has its own lifespan, velocity
// with optional drag, color and size; the renderer draws fading discs.
//
// All spawn helpers wrap the common `spawnParticle` factory. Use the
// purpose-named ones (spawnDust, spawnSparks, spawnFootstep) from gameplay
// systems to keep call sites readable.

import type { Particle, World } from '../world/types.js';

export interface ParticleOptions {
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  life: number;
  color: number;
  size: number;
  drag?: number;
  shape?: Particle['shape'];
}

export function spawnParticle(world: World, opts: ParticleOptions): void {
  world.particles.push({
    x: opts.x,
    y: opts.y,
    vx: opts.vx ?? 0,
    vy: opts.vy ?? 0,
    life: opts.life,
    maxLife: opts.life,
    color: opts.color,
    size: opts.size,
    drag: opts.drag ?? 2,
    shape: opts.shape ?? 'circle',
  });
}

/** Soft gray cloud, used on K.O. */
export function spawnDust(world: World, x: number, y: number, count = 10): void {
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const speed = 30 + Math.random() * 50;
    spawnParticle(world, {
      x,
      y,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      life: 0.6 + Math.random() * 0.4,
      color: 0x8a8590,
      size: 3 + Math.random() * 2.5,
      drag: 4,
    });
  }
}

/** Bright yellow short-lived sparks, used on knock. */
export function spawnSparks(world: World, x: number, y: number, dir: number, count = 6): void {
  for (let i = 0; i < count; i++) {
    const spread = ((Math.random() - 0.5) * Math.PI) / 2; // ±45°
    const a = dir + spread;
    const speed = 80 + Math.random() * 60;
    spawnParticle(world, {
      x,
      y,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      life: 0.3 + Math.random() * 0.2,
      color: 0xffd870,
      size: 1.6 + Math.random() * 1.2,
      drag: 5,
      shape: 'spark',
    });
  }
}

/** Tiny floor scuff behind the player on each step. */
export function spawnFootstep(world: World, x: number, y: number, dir: number): void {
  // Drop slightly behind the direction of travel
  const back = dir + Math.PI;
  const offX = Math.cos(back) * 4;
  const offY = Math.sin(back) * 4;
  spawnParticle(world, {
    x: x + offX + (Math.random() - 0.5) * 4,
    y: y + offY + (Math.random() - 0.5) * 4 + 4,
    vx: 0,
    vy: 0,
    life: 0.45,
    color: 0xa0acc0,
    size: 2.2,
    drag: 0,
  });
}

/** Golden firework burst, used on the win toast for top-tier ranks. */
export function spawnFirework(world: World, x: number, y: number, count = 24): void {
  for (let i = 0; i < count; i++) {
    const a = (Math.PI * 2 * i) / count + Math.random() * 0.2;
    const speed = 90 + Math.random() * 120;
    spawnParticle(world, {
      x,
      y,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed,
      life: 0.9 + Math.random() * 0.4,
      color: i % 3 === 0 ? 0xffffff : i % 3 === 1 ? 0xffe080 : 0xffaa40,
      size: 2.5 + Math.random() * 2,
      drag: 1.6,
      shape: 'spark',
    });
  }
}

/** Cyan trail behind a thrown decoy, called per frame while in flight. */
export function spawnDecoyTrail(world: World, x: number, y: number): void {
  spawnParticle(world, {
    x: x + (Math.random() - 0.5) * 2,
    y: y + (Math.random() - 0.5) * 2,
    vx: (Math.random() - 0.5) * 20,
    vy: (Math.random() - 0.5) * 20,
    life: 0.35,
    color: 0x80d8ff,
    size: 2,
    drag: 4,
  });
}

/** Per-frame update — moves particles, applies drag, removes expired. */
export function updateParticles(world: World, dt: number): void {
  const ps = world.particles;
  for (let i = ps.length - 1; i >= 0; i--) {
    const p = ps[i]!;
    p.life -= dt;
    if (p.life <= 0) {
      ps.splice(i, 1);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    if (p.drag > 0) {
      const k = Math.exp(-p.drag * dt);
      p.vx *= k;
      p.vy *= k;
    }
  }
}
